/* eslint-disable no-param-reassign -- the engine's whole job is writing text, CSS variables and state classes onto the nodes it is handed */
// Live instruments of the "Sua vantagem competitiva" cards (FPS Boost, Multiconexão mini panel, AI routing),
// ported from the design's `initEdge` + `mcMirror`. React renders the markup once; this engine only writes
// text, CSS variables and state classes on those nodes, and `destroy()` undoes every timer and observer.
import type { McPlace } from '../geo/geo';
import { MC_PLACE_EVENT, readMcPlace } from '../geo/geo';

export interface EdgeStrings {
  live: string;
  stable: string;
  withBoost: string;
  scanning: string;
  route: (code: string) => string;
  worse: (code: string) => string;
}

interface MirrorRow {
  code: string;
  city: string;
  ms: number;
  win: boolean;
}

interface MirrorSnap {
  rows: MirrorRow[];
  code: string;
  city: string;
  ms: number;
  hit: string;
  worst: number;
}

interface MirrorPlace {
  routes: Array<{ city: string; code?: string }>;
  scenarios: Array<Record<string, number>>;
}

const IDS = ['r1', 'r2', 'r3', 'r4', 'r5'] as const;

const FALLBACK: MirrorPlace = {
  routes: [
    { city: 'São Paulo', code: 'SAO' },
    { city: 'Rio de Janeiro', code: 'RIO' },
    { city: 'Buenos Aires', code: 'BUE' },
    { city: 'Santiago', code: 'SCL' },
    { city: 'Miami', code: 'MIA' },
  ],
  scenarios: [
    { r1: 3, r2: 5, r3: 11, r4: 14, r5: 19 },
    { r1: 8, r2: 3, r3: 10, r4: 15, r5: 18 },
    { r1: 2, r2: 4, r3: 11, r4: 13, r5: 19 },
    { r1: 9, r2: 2, r3: 12, r4: 14, r5: 20 },
    { r1: 3, r2: 5, r3: 10, r4: 14, r5: 18 },
  ],
};

function randomInt(low: number, high: number): number {
  return low + Math.floor(Math.random() * (high - low + 1));
}

function routeCode(route: { city?: string; code?: string }): string {
  return String(route.code || route.city || '')
    .toUpperCase()
    .slice(0, 3);
}

function usable(candidate: unknown): candidate is MirrorPlace {
  const place = candidate as Partial<McPlace> | null | undefined;
  if (!place || !Array.isArray(place.routes) || place.routes.length < 5 || !Array.isArray(place.scenarios) || !place.scenarios.length) return false;
  const firstScenario = place.scenarios[0];
  return IDS.every((id, index) => {
    const route = place.routes?.[index];
    return Boolean(route && route.city) && typeof firstScenario?.[id] === 'number';
  });
}

type Subscriber = (snap: MirrorSnap) => void;

/** Mirror of the Multiconexão card: the same routes and winner, sampled on its own 0.5 s beat. */
export function createMcMirror() {
  let place: MirrorPlace = FALLBACK;
  let hasPlace = false;
  let order: number[] = [];
  let scenarioIndex = 0;
  let beat = 0;
  let values: number[] = [];
  let winner = -1;
  let hit = -1;
  let hitLeft = 0;
  let timer = 0;
  let snap: MirrorSnap | null = null;
  const subscribers: Subscriber[] = [];
  const wanted: Record<string, boolean> = {};

  const scenarioValue = (scenario: Record<string, number> | undefined, index: number) => Number(scenario?.[IDS[index] ?? ''] ?? 0) || 0;

  function sample() {
    const scenario = place.scenarios[scenarioIndex % place.scenarios.length];
    let best = 0;
    for (let index = 1; index < 5; index += 1) if (scenarioValue(scenario, index) < scenarioValue(scenario, best)) best = index;
    const top = Math.max(2, Math.min(5, scenarioValue(scenario, best) + randomInt(-1, 1)));
    values = IDS.map((_id, index) => (index === best ? top : Math.max(top + 1, scenarioValue(scenario, index) + randomInt(-1, 1))));
    if (hitLeft > 0) {
      hitLeft -= 1;
      if (hitLeft === 0) hit = -1;
    }
    if (winner >= 0 && best !== winner) {
      // the old leader lost the lead: "SAO piorou" for ~1.5 s
      hit = winner;
      hitLeft = 3;
    }
    winner = best;
  }

  function emit() {
    const winnerRoute = place.routes[winner] ?? { city: '' };
    snap = {
      rows: order.map((routeIndex) => ({ code: routeCode(place.routes[routeIndex] ?? { city: '' }), city: place.routes[routeIndex]?.city ?? '', ms: values[routeIndex] ?? 0, win: routeIndex === winner })),
      code: routeCode(winnerRoute),
      city: winnerRoute.city,
      ms: values[winner] ?? 0,
      hit: hit >= 0 ? routeCode(place.routes[hit] ?? { city: '' }) : '',
      worst: Math.max(...values),
    };
    const current = snap;
    subscribers.forEach((subscriber) => subscriber(current));
  }

  function load(candidate: unknown) {
    if (usable(candidate)) {
      place = candidate;
      hasPlace = true;
    } else if (hasPlace && snap) {
      return; // an unusable profile keeps what is shown
    } else {
      place = FALLBACK;
    }
    const firstScenario = place.scenarios[0];
    order = [0, 1, 2, 3, 4].sort((first, second) => scenarioValue(firstScenario, first) - scenarioValue(firstScenario, second) || first - second);
    scenarioIndex = 0;
    beat = 0;
    winner = -1;
    hit = -1;
    hitLeft = 0;
    sample();
    emit();
  }

  function tick() {
    beat += 1;
    if (beat % 10 === 0) scenarioIndex = (scenarioIndex + 1) % place.scenarios.length;
    sample();
    emit();
  }

  function sync() {
    const on = Object.values(wanted).some(Boolean) && !document.hidden;
    if (on && !timer) timer = window.setInterval(tick, 500);
    else if (!on && timer) {
      window.clearInterval(timer);
      timer = 0;
    }
  }

  const onPlace = (event: Event) => load((event as CustomEvent<unknown>).detail);
  document.addEventListener('visibilitychange', sync);
  window.addEventListener(MC_PLACE_EVENT, onPlace);
  load(readMcPlace());

  return {
    snap: () => snap,
    on: (subscriber: Subscriber) => subscribers.push(subscriber),
    want: (who: string, value: boolean) => {
      wanted[who] = value;
      sync();
    },
    destroy: () => {
      window.clearInterval(timer);
      timer = 0;
      subscribers.length = 0;
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener(MC_PLACE_EVENT, onPlace);
    },
  };
}

interface Instrument {
  arm: () => void;
  enter: () => void;
  resume: () => void;
  pause: () => void;
}

/** Starts the three instruments inside `section`. Returns the teardown. */
export function startEdge(section: HTMLElement, strings: EdgeStrings, htmlLang: string, reducedMotion: boolean): () => void {
  const vizzes = Array.from(section.querySelectorAll<HTMLElement>('[data-viz]'));
  if (!vizzes.length) return () => {};
  const mirror = createMcMirror();
  const timeouts = new Set<number>();
  const intervals = new Set<number>();
  const frames = new Set<number>();
  const observers: IntersectionObserver[] = [];

  const later = (callback: () => void, delay: number) => {
    const id = window.setTimeout(() => {
      timeouts.delete(id);
      callback();
    }, delay);
    timeouts.add(id);
  };

  let seed = 51413;
  const random = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const between = (low: number, high: number) => low + random() * (high - low);
  const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));
  const ease = (progress: number) => 1 - (1 - progress) ** 3;
  const thousand = new Intl.NumberFormat(htmlLang);

  function count(element: Element, from: number, to: number, duration: number, format: (value: number) => string, done?: () => void) {
    let start = 0;
    element.textContent = format(from);
    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = Math.min(1, (timestamp - start) / duration);
      element.textContent = format(Math.round(from + (to - from) * ease(progress)));
      if (progress < 1) {
        const id = window.requestAnimationFrame(step);
        frames.add(id);
      } else if (done) done();
    };
    frames.add(window.requestAnimationFrame(step));
  }

  // counts an element to its data-viz-count (from data-viz-from), keeping its suffix
  function countAttr(element: Element, duration: number, done?: () => void) {
    const formatKind = element.getAttribute('data-viz-format');
    const suffix = element.getAttribute('data-viz-suffix') || '';
    count(element, parseFloat(element.getAttribute('data-viz-from') || '0'), parseFloat(element.getAttribute('data-viz-count') || '0'), duration, (value) => (formatKind === 'thousand' ? thousand.format(value) : String(value)) + suffix, done);
  }

  const screen = (element: HTMLElement) => ({ status: element.querySelector('[data-viz-st]'), big: element.querySelector('[data-viz-big]'), pill: element.querySelector('[data-viz-pill]') });
  const say = (node: Element | null | undefined, text: string) => {
    if (node && node.textContent !== text) node.textContent = text;
  };

  // one affine scale over the 2–40 ms band with a floor, so a 2 ms winner is a short but visible bar
  const MS_TOP = 42;
  const MS_FLOOR = 6;
  const msWidth = (ms: number) => `${(MS_FLOOR + (Math.min(ms, MS_TOP) / MS_TOP) * (100 - MS_FLOOR)).toFixed(1)}%`;

  const routeRows = (element: HTMLElement) => Array.from(element.querySelectorAll<HTMLElement>('.v1-viz-routes__row')).map((row) => ({ row, code: row.firstElementChild, bar: row.querySelector<HTMLElement>('.v1-viz-routes__bar i'), num: row.querySelector('[data-route-ms]') }));

  function paintRoutes(element: HTMLElement, rows: ReturnType<typeof routeRows>, snap: Pick<MirrorSnap, 'rows' | 'code' | 'ms' | 'hit'>, withNumbers = true) {
    const parts = screen(element);
    rows.forEach((row, index) => {
      const data = snap.rows[index];
      if (!data) return;
      say(row.code, data.code);
      if (withNumbers) say(row.num, String(data.ms));
      row.row.setAttribute('data-ms', String(data.ms));
      row.bar?.style.setProperty('--w', msWidth(data.ms));
      row.row.classList.toggle('v1-viz-routes__row--win', data.win);
    });
    if (withNumbers) say(parts.big, String(snap.ms));
    say(parts.pill, strings.route(snap.code));
    say(parts.status, snap.hit ? strings.worse(snap.hit) : strings.live);
    element.classList.toggle('is-alert', Boolean(snap.hit));
  }

  const aiParts = (element: HTMLElement) => ({ best: element.querySelector('[data-viz-cycle]'), big: element.querySelector('.v1-viz__foot .v1-viz__big') });
  function paintAi(element: HTMLElement, snap: { city: string; ms: number }) {
    const parts = aiParts(element);
    say(parts.best, snap.city);
    say(parts.big, String(snap.ms));
    parts.big?.setAttribute('data-viz-count', String(snap.ms));
  }

  const stillRows = (snap: MirrorSnap) => snap.rows.map((row, index) => ({ ...row, win: index === 0 }));

  // Still frame (reduced motion, no IntersectionObserver): nothing moves, but the routes and the AI card
  // still name the visitor's cities once the big card has published them.
  if (reducedMotion || typeof IntersectionObserver === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    const still = (snap: MirrorSnap) => {
      const rows = stillRows(snap);
      const first = rows[0];
      if (!first) return;
      vizzes.forEach((element) => {
        const kind = element.getAttribute('data-viz');
        if (kind === 'routes') paintRoutes(element, routeRows(element), { rows, code: first.code, ms: first.ms, hit: '' });
        if (kind === 'ai') paintAi(element, first);
      });
    };
    mirror.on(still);
    const initial = mirror.snap();
    if (initial) still(initial);
    return () => mirror.destroy();
  }

  // FPS Boost: bars grow and count, then live: every ~0.5 s "Antes" wanders 38–80 FPS and "Com Boost"
  // 280–320 FPS on one scale (320 FPS = 100 %); the frametime strip scrolls the boosted run's frame times.
  function fps(element: HTMLElement): Instrument {
    const numbers = Array.from(element.querySelectorAll('.v1-viz-fps__v [data-viz-count]'));
    const bars = Array.from(element.querySelectorAll<HTMLElement>('.v1-viz-fps__bar i'));
    const strip = Array.from(element.querySelectorAll<HTMLElement>('.v1-viz-fps__frames i'));
    const parts = screen(element);
    const FULL = 320;
    const current = { before: 69, boosted: 300 };
    let history: number[] = [];
    let timer = 0;
    let ticks = 0;
    let live = false;
    const width = (value: number) => `${Math.min(100, (value / FULL) * 100).toFixed(1)}%`;
    const pick = () => {
      current.before = Math.round(clamp(current.before + between(-10, 10) + (60 - current.before) * 0.2, 38, 80));
      current.boosted = Math.round(clamp(current.boosted + between(-14, 14) + (300 - current.boosted) * 0.25, 280, 320));
    };
    const sampleFrame = () => clamp(20 + ((1000 / current.boosted - 3.125) / 0.446) * 16 + between(-2.5, 2.5), 14, 40);
    const setBars = () => {
      bars[0]?.style.setProperty('--w', width(current.before));
      bars[1]?.style.setProperty('--w', width(current.boosted));
    };
    const paintNumbers = () => {
      say(numbers[0], String(current.before));
      say(numbers[1], String(current.boosted));
      setBars();
      say(parts.big, String(current.boosted));
    };
    const paintStrip = () => {
      strip.forEach((bar, index) => {
        bar.style.setProperty('--h', `${(history[index] ?? 30).toFixed(1)}%`);
        if (bar.className) bar.className = '';
      });
    };
    const tick = () => {
      if (history.length !== strip.length) return; // never before the entrance has filled the strip
      history.shift();
      history.push(sampleFrame());
      paintStrip();
      ticks += 1;
      if (live && ticks % 5 === 0) {
        pick();
        paintNumbers();
      }
    };
    return {
      arm: () => {
        element.classList.add('is-armed');
        numbers.forEach((number) => {
          number.textContent = '0';
        });
        say(parts.big, '0');
        say(parts.status, strings.stable);
        say(parts.pill, strings.withBoost);
      },
      enter: () => {
        pick();
        numbers[0]?.setAttribute('data-viz-count', String(current.before));
        numbers[1]?.setAttribute('data-viz-count', String(current.boosted));
        setBars();
        element.classList.remove('is-armed');
        numbers.forEach((number) => countAttr(number, 1100));
        if (parts.big) count(parts.big, 0, current.boosted, 1100, String);
        history = strip.map(sampleFrame);
        paintStrip();
        later(() => {
          live = true;
          element.classList.add('is-ticking');
        }, 1200);
      },
      resume: () => {
        if (!timer) {
          timer = window.setInterval(tick, 110);
          intervals.add(timer);
        }
      },
      pause: () => {
        window.clearInterval(timer);
        intervals.delete(timer);
        timer = 0;
      },
    };
  }

  // Multiconexão: the mirror's five routes, the winner lit and read out big (pill "Rota XXX"); when the lead
  // changes hands the old leader is named in red for ~1.5 s ("SAO piorou").
  function routes(element: HTMLElement): Instrument {
    const rows = routeRows(element);
    const parts = screen(element);
    let live = false;
    mirror.on((snap) => {
      if (live) paintRoutes(element, rows, snap);
    });
    return {
      arm: () => {
        element.classList.add('is-armed');
        rows.forEach((row) => {
          if (row.num) row.num.textContent = '0';
        });
        say(parts.big, '0');
      },
      enter: () => {
        const snap = mirror.snap();
        if (!snap) return;
        paintRoutes(element, rows, snap, false);
        element.classList.remove('is-armed');
        rows.forEach((row, index) => {
          row.bar?.style.setProperty('--td', `${index * 80}ms`);
          if (row.num) count(row.num, 0, snap.rows[index]?.ms ?? 0, 900 + index * 80, String);
        });
        if (parts.big) count(parts.big, 0, snap.ms, 900, String);
        later(() => {
          rows.forEach((row) => row.bar?.style.removeProperty('--td'));
          live = true;
        }, 1400);
      },
      resume: () => mirror.want('routes', true),
      pause: () => mirror.want('routes', false),
    };
  }

  // Roteamento por IA: the read-outs reveal one by one while the status says "Escaneando"; "Melhor caminho"
  // flicks through the candidate cities and settles on the winner; then it follows the mirror's winner.
  function ai(element: HTMLElement): Instrument {
    const lines = Array.from(element.querySelectorAll<HTMLElement>('[data-viz-line]'));
    const parts = screen(element);
    const aiRead = aiParts(element);
    let live = false;
    mirror.on((snap) => {
      if (live) paintAi(element, snap);
    });
    const cycle = (target: Element) => {
      const names = (target.getAttribute('data-viz-cycle') || '').split('|');
      let index = 0;
      target.textContent = names[0] ?? '';
      const id = window.setInterval(() => {
        index += 1;
        target.textContent = names[index] ?? '';
        if (index >= names.length - 1) {
          window.clearInterval(id);
          intervals.delete(id);
        }
      }, 140);
      intervals.add(id);
    };
    return {
      arm: () => {
        element.classList.add('is-armed');
        say(parts.status, strings.scanning);
        parts.pill?.classList.remove('is-on');
      },
      enter: () => {
        const snap = mirror.snap();
        if (!snap) return;
        aiRead.best?.setAttribute(
          'data-viz-cycle',
          snap.rows
            .filter((row) => !row.win)
            .reverse()
            .map((row) => row.city)
            .concat(snap.city)
            .join('|')
        );
        if (aiRead.big) {
          aiRead.big.setAttribute('data-viz-from', String(snap.worst));
          aiRead.big.setAttribute('data-viz-count', String(snap.ms));
        }
        lines.forEach((line, index) => {
          later(
            () => {
              line.classList.add('is-in');
              const target = line.querySelector('[data-viz-count], [data-viz-cycle]');
              const done =
                index === lines.length - 1
                  ? () => {
                      parts.pill?.classList.add('is-on');
                      say(parts.status, strings.live);
                      element.classList.remove('is-armed');
                      live = true;
                      const now = mirror.snap();
                      if (now) paintAi(element, now);
                    }
                  : undefined;
              if (!target) return;
              if (target.hasAttribute('data-viz-count')) countAttr(target, 800, done);
              else cycle(target);
            },
            120 + index * 300
          );
        });
      },
      resume: () => mirror.want('ai', true),
      pause: () => mirror.want('ai', false),
    };
  }

  const KINDS: Record<string, (element: HTMLElement) => Instrument> = { fps, routes, ai };
  const instruments: Instrument[] = [];
  vizzes.forEach((element, vizIndex) => {
    const make = KINDS[element.getAttribute('data-viz') ?? ''];
    if (!make) return;
    const instrument = make(element);
    instruments.push(instrument);
    const card = element.closest('.v1-feat') || element;
    let entered = false;
    let ready = false;
    let onScreen = false;
    const run = () => {
      element.classList.add('is-live');
      instrument.resume();
    };
    const hold = () => {
      element.classList.remove('is-live');
      instrument.pause();
    };
    instrument.arm();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          onScreen = entry.isIntersecting;
          if (!onScreen) {
            hold();
            return;
          }
          if (entered) {
            if (ready) run();
            return;
          }
          if (entry.intersectionRatio < 0.35) return;
          entered = true;
          later(
            () => {
              ready = true;
              instrument.enter();
              if (onScreen) run();
            },
            180 + vizIndex * 120
          );
        });
      },
      { threshold: [0, 0.35] }
    );
    observer.observe(card);
    observers.push(observer);
  });

  return () => {
    observers.forEach((observer) => observer.disconnect());
    instruments.forEach((instrument) => instrument.pause());
    timeouts.forEach((id) => window.clearTimeout(id));
    intervals.forEach((id) => window.clearInterval(id));
    frames.forEach((id) => window.cancelAnimationFrame(id));
    mirror.destroy();
  };
}
